<template>
  <div class="legal-page">
    <div class="legal-page__header">
      <h1 class="legal-page__title">Terms of Service</h1>
      <p class="legal-page__date">Effective Date: January 1, 2024</p>
    </div>

    <div class="legal-page__card">
      <p class="legal-text">
        Welcome to Thinking of U! By accessing or using our website, mobile applications, and related services (collectively, the "Services"), you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any part of these Terms, please do not use our Services.
      </p>

      <h3 class="legal-subhead">1. Acceptance of Terms</h3>
      <p class="legal-text">
        By accessing or using the Services, you agree to these Terms of Service, including any future modifications. These Terms, together with our Privacy Policy and any related policies (e.g., Cookie Policy), constitute the entire agreement between you and Thinking of U.
      </p>

      <h3 class="legal-subhead">2. Changes to Terms</h3>
      <p class="legal-text">
        We may update these Terms at any time. Significant changes will be communicated by updating the effective date above and, when appropriate, by notifying you via email. Your continued use of our Services after such updates constitutes acceptance of the revised Terms.
      </p>

      <h3 class="legal-subhead">3. Use of Services</h3>
      <p class="legal-text">
        Thinking of U provides a platform for creating and sending personalized postcards. You agree to use the Services only for lawful purposes and in accordance with these Terms. You must not misuse or interfere with our Services or attempt unauthorized access.
      </p>

      <h3 class="legal-subhead">4. AI-Generated Content Disclaimer</h3>
      <p class="legal-text">
        Our Services use artificial intelligence (AI) to generate written messages and visual content based on your inputs. All AI-generated content is provided "as is" without warranties of any kind.
      </p>
      <p class="legal-text">
        While we strive to ensure high-quality, meaningful outputs, we do not guarantee the accuracy, appropriateness, or suitability of generated content. You acknowledge that content may sometimes be unpredictable or reflect unintended tone. Use of this content is at your own discretion and risk.
      </p>
      <p class="legal-text">
        If you have concerns about any generated output, please contact us at
        <a href="mailto:contact@thinking-of-u.com">contact@thinking-of-u.com</a> or via our
        <router-link to="/contact-us">Contact Us</router-link> page.
      </p>

      <h3 class="legal-subhead">5. User Accounts</h3>
      <p class="legal-text">
        To access certain features, you may need to create an account. You are responsible for safeguarding your credentials and for any activity under your account. Notify us immediately if you believe your account has been compromised.
      </p>

      <h3 class="legal-subhead">6. Content</h3>
      <p class="legal-text">
        You retain ownership of content you submit through the Services. By using the Services, you grant Thinking of U a non-exclusive, royalty-free license to use, display, reproduce, and distribute your content as necessary to operate and improve the Services.
      </p>
      <p class="legal-text">
        This license is limited to activities such as generating postcards, fulfilling orders, and maintaining product quality. We will never share your private content publicly without your consent.
      </p>

      <h3 class="legal-subhead">7. Prohibited Activities</h3>
      <p class="legal-text">You agree not to use the Services to:</p>
      <ul class="legal-list">
        <li>Engage in illegal, fraudulent, or unauthorized activity.</li>
        <li>Transmit viruses, malware, or harmful content.</li>
        <li>Harass, threaten, defame, or abuse others.</li>
        <li>Violate applicable laws or third-party rights.</li>
      </ul>

      <h3 class="legal-subhead">8. Payment & Third-Party Services</h3>
      <p class="legal-text">
        Payments are securely processed by Stripe. We do not store full credit card details. Your use of third-party services (including Stripe, OpenAI, Firebase, or others) may be subject to their respective terms and privacy policies.
      </p>

      <h3 class="legal-subhead">9. Termination</h3>
      <p class="legal-text">
        We may suspend or terminate your access to the Services at any time, without notice, for conduct that violates these Terms or poses a risk to the platform. Upon termination, your right to use the Services will cease immediately.
      </p>

      <h3 class="legal-subhead">10. Limitation of Liability</h3>
      <p class="legal-text">
        To the maximum extent permitted by law, Thinking of U shall not be liable for any indirect, incidental, special, or consequential damages, including loss of profits or data, arising from or related to your use of the Services. Our total liability shall not exceed the amount you paid, if any, for use of the Services.
      </p>

      <h3 class="legal-subhead">11. Governing Law</h3>
      <p class="legal-text">
        These Terms shall be governed by the laws of the jurisdiction in which Thinking of U is registered or operates, without regard to conflict of law provisions.
      </p>

      <h3 class="legal-subhead">12. Dispute Resolution</h3>
      <p class="legal-text">
        In the event of a dispute, we encourage you to contact us first to seek a resolution. If unresolved, and where permitted by applicable law, disputes will be submitted to binding arbitration under the rules of the American Arbitration Association (AAA) in the jurisdiction where Thinking of U operates. Arbitration will be the exclusive remedy unless otherwise required by law.
      </p>

      <h3 class="legal-subhead">13. Contact Us</h3>
      <p class="legal-text">
        If you have any questions about these Terms or your rights, contact us at
        <a href="mailto:contact@thinking-of-u.com">contact@thinking-of-u.com</a> or visit our
        <router-link to="/contact-us">Contact Us</router-link> page.
      </p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TermsOfService',
};
</script>

<style scoped>
.legal-page {
  background: linear-gradient(180deg, var(--light-pink) 0%, #f5f0fa 100%);
  min-height: 100vh;
  padding: 0 1rem 3rem;
}

.legal-page__header {
  text-align: center;
  padding: 3rem 1rem 1.5rem;
}

.legal-page__title {
  font-size: 2.2rem;
  background: linear-gradient(135deg, var(--hollywood-cerise), var(--violet-lush));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0.5rem;
}

.legal-page__date {
  font-size: 0.9rem;
  color: #888;
  font-style: italic;
}

.legal-page__card {
  max-width: 800px;
  margin: 0 auto;
  padding: 2.5rem;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 20px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.06);
  animation: cardIn 0.5s ease both;
}

@keyframes cardIn {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

.legal-subhead {
  font-size: 1.15rem;
  color: var(--hollywood-cerise);
  margin: 2rem 0 0.75rem;
  font-weight: 600;
}

.legal-text {
  font-size: 0.98rem;
  line-height: 1.8;
  color: #444;
  margin-bottom: 1rem;
}

.legal-text a,
.legal-text a:visited {
  color: var(--hollywood-cerise);
  text-decoration: none;
  font-weight: 500;
}

.legal-text a:hover {
  text-decoration: underline;
}

.legal-list {
  padding-left: 0;
  margin-bottom: 1rem;
  list-style-position: inside;
}

.legal-list li {
  font-size: 0.98rem;
  line-height: 1.8;
  color: #444;
  margin-bottom: 0.5rem;
}

.legal-list li strong {
  color: var(--violet-lush);
}

@media (max-width: 768px) {
  .legal-page__title { font-size: 1.8rem; }
  .legal-page__card { padding: 1.5rem; border-radius: 16px; }
}
</style>
